/**
 * Haven Black Friday Edit (bf26 sections).
 *
 * - <bf26-card>: a product card with option buttons and an Add to cart that stays on the page.
 *   Its variants, already narrowed to the ones in the edit, come as JSON inside the card. After an
 *   add, the theme's cart:update event refreshes the cart count in the header.
 * - <bf26-jump-nav>: marks the "In this edit" link for the section in view, and whether the bar is
 *   stuck under the header (frosted, or shown at all for a "reveal" bar).
 * - <bf26-scroller>: lights the dot of the card in view in a sideways row on phones.
 * - <bf26-countdown>: counts down to the end of the offer, then hides itself.
 */

import { CartAddEvent } from '@theme/events';

/**
 * @typedef {object} Bf26Variant
 * @property {number} id
 * @property {string[]} options
 * @property {boolean} available
 * @property {string} now - Price, formatted
 * @property {string} was - Compare-at price, formatted, or empty
 * @property {string} save - "Save 40%" style text, or empty
 * @property {string} src - Image URL, or empty to keep the current image
 * @property {string} srcset
 */

/**
 * A picture set on the page for some option values, e.g. a king bed for "king".
 * @typedef {object} Bf26Picture
 * @property {string[]} values - Option values, lowercase
 * @property {string} src
 * @property {string} srcset
 */

class Bf26Card extends HTMLElement {
  /** @type {Bf26Variant[]} */
  #variants = [];

  /** @type {string[]} */
  #selected = [];

  /** @type {number[]} Positions of the options that have buttons. */
  #shown = [];

  /** @type {Bf26Picture[]} */
  #pictures = [];

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    try {
      this.#variants = JSON.parse(this.querySelector('[data-bf26-variants]')?.textContent || '[]');
    } catch {
      this.#variants = [];
    }

    try {
      this.#pictures = JSON.parse(this.querySelector('[data-bf26-pictures]')?.textContent || '[]');
    } catch {
      this.#pictures = [];
    }

    const idInput = this.querySelector('[data-bf26-id]');
    const startId = idInput instanceof HTMLInputElement ? idInput.value : '';
    const current = this.#variants.find((variant) => String(variant.id) === startId) ?? this.#variants[0];
    this.#selected = current ? [...current.options] : [];
    this.#shown = Array.from(this.querySelectorAll('[data-bf26-option]'), (group) =>
      Number(group.getAttribute('data-bf26-option'))
    );

    this.addEventListener(
      'click',
      (event) => {
        const pill = event.target instanceof Element ? event.target.closest('.bf26-pill') : null;
        const group = pill?.closest('[data-bf26-option]');
        if (!pill || !group || !this.contains(group)) return;
        this.#choose(Number(group.getAttribute('data-bf26-option')), pill.getAttribute('data-value') ?? '');
      },
      { signal }
    );

    this.querySelector('[data-bf26-form]')?.addEventListener('submit', this.#handleSubmit, { signal });

    this.#update(current, false);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * @param {number} index - Option position
   * @param {string} value - Option value picked
   */
  #choose(index, value) {
    this.#selected[index] = value;
    let match = this.#variants.find((variant) => this.#shown.every((i) => variant.options[i] === this.#selected[i]));

    // Nothing has this exact combination: keep the value just picked and switch the others to fit.
    if (!match) {
      const withValue = this.#variants.filter((variant) => variant.options[index] === value);
      match = withValue.find((variant) => variant.available) ?? withValue[0];
    }

    if (match) this.#selected = [...match.options];
    this.#update(match, true);
  }

  /**
   * @param {Bf26Variant | undefined} variant
   * @param {boolean} changed - Whether a shopper picked it (rather than the initial render)
   */
  #update(variant, changed) {
    for (const group of this.querySelectorAll('[data-bf26-option]')) {
      const index = Number(group.getAttribute('data-bf26-option'));
      let pressedLabel = '';

      for (const pill of group.querySelectorAll('.bf26-pill')) {
        const value = pill.getAttribute('data-value');
        const pressed = value === this.#selected[index];
        pill.setAttribute('aria-pressed', String(pressed));
        if (pressed) pressedLabel = pill.textContent?.trim() ?? '';

        // Dim values that don't come with the other current choices. They can still be picked.
        const fits = this.#variants.some(
          (v) => v.options[index] === value && this.#shown.every((i) => i === index || v.options[i] === this.#selected[i])
        );
        pill.classList.toggle('is-unavailable', !fits);
      }

      const label = group.querySelector('[data-bf26-option-value]');
      if (label) label.textContent = pressedLabel;
    }

    const button = this.querySelector('[data-bf26-add]');
    const idInput = this.querySelector('[data-bf26-id]');

    if (!variant) {
      if (button instanceof HTMLButtonElement) {
        button.disabled = true;
        button.textContent = this.dataset.labelUnavailable ?? '';
      }
      return;
    }

    if (idInput instanceof HTMLInputElement) idInput.value = String(variant.id);

    const productUrl = this.dataset.productUrl;
    if (productUrl) {
      for (const link of this.querySelectorAll('[data-bf26-link]')) {
        link.setAttribute('href', `${productUrl}?variant=${variant.id}`);
      }
    }

    this.#setText('[data-bf26-now]', variant.now);
    this.#setText('[data-bf26-was-value]', variant.was);
    this.querySelector('[data-bf26-was]')?.toggleAttribute('hidden', !variant.was);

    const save = this.querySelector('[data-bf26-save]');
    if (save) {
      save.textContent = variant.save;
      save.toggleAttribute('hidden', !variant.save);
    }

    if (changed) {
      const picture = this.#pictureFor(variant);
      const image = this.querySelector('[data-bf26-image]');
      if (picture?.src && image instanceof HTMLImageElement && image.getAttribute('src') !== picture.src) {
        image.srcset = picture.srcset || '';
        image.src = picture.src;
      }
    }

    if (button instanceof HTMLButtonElement) {
      button.disabled = !variant.available;
      button.textContent = variant.available ? this.dataset.labelAdd ?? '' : this.dataset.labelSoldOut ?? '';
    }

    if (changed) this.#setStatus('');
  }

  /**
   * The picture for a variant: one set on the page for any of its option values, else the card's own
   * picture when there are page pictures, else the variant's picture.
   * @param {Bf26Variant} variant
   * @returns {{ src: string, srcset: string } | null}
   */
  #pictureFor(variant) {
    const options = variant.options.map((option) => option.toLowerCase());
    const match = this.#pictures.find((picture) => picture.values.some((value) => options.includes(value)));
    if (match) return match;
    if (this.#pictures.length && this.dataset.defaultSrc) {
      return { src: this.dataset.defaultSrc, srcset: this.dataset.defaultSrcset ?? '' };
    }
    return variant.src ? { src: variant.src, srcset: variant.srcset } : null;
  }

  /** @param {SubmitEvent} event */
  #handleSubmit = async (event) => {
    event.preventDefault();

    const button = this.querySelector('[data-bf26-add]');
    const idInput = this.querySelector('[data-bf26-id]');
    if (!(button instanceof HTMLButtonElement) || !(idInput instanceof HTMLInputElement)) return;

    const id = Number(idInput.value);
    button.disabled = true;
    button.textContent = this.dataset.labelAdding ?? '';
    this.#setStatus('');

    try {
      const response = await fetch(`${this.dataset.cartAddUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || '');

      const cart = await (await fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' } })).json();
      this.dispatchEvent(
        new CartAddEvent(cart, this.id, { source: 'bf26-card', itemCount: cart.item_count, variantId: String(id) })
      );

      button.textContent = this.dataset.labelAdded ?? '';
      this.#setStatus(this.dataset.labelAddedStatus ?? '', true);
    } catch (error) {
      button.textContent = this.dataset.labelAdd ?? '';
      // Network failures read "Failed to fetch"; show the friendly message instead.
      const message = error instanceof Error && !(error instanceof TypeError) ? error.message : '';
      this.#setStatus(message || this.dataset.labelError || '');
    } finally {
      button.disabled = false;
    }
  };

  /**
   * @param {string} selector
   * @param {string} text
   */
  #setText(selector, text) {
    const element = this.querySelector(selector);
    if (element) element.textContent = text;
  }

  /**
   * Shows a message under the button, followed by a link to the cart after an add.
   * @param {string} message
   * @param {boolean} [withCartLink]
   */
  #setStatus(message, withCartLink = false) {
    const status = this.querySelector('[data-bf26-status]');
    if (!status) return;

    status.textContent = message;
    if (withCartLink && this.dataset.cartUrl) {
      const link = document.createElement('a');
      link.href = this.dataset.cartUrl;
      link.textContent = this.dataset.labelViewCart ?? '';
      status.append(' ', link);
    }
  }
}

class Bf26JumpNav extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {(HTMLElement | null)[]} */
  #targets = [];

  #frame = 0;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const links = Array.from(this.querySelectorAll('a[href^="#"]'));
    this.#targets = links.map((link) => {
      const id = decodeURIComponent((link.getAttribute('href') ?? '').slice(1));
      return id ? document.getElementById(id) : null;
    });

    // Solid in the page, frosted once it sticks under the header. A "reveal" bar only shows then.
    if (this.classList.contains('bf26-jump--sticky')) {
      window.addEventListener('scroll', this.#queueStuck, { passive: true, signal });
      window.addEventListener('resize', this.#queueStuck, { passive: true, signal });
      this.#updateStuck();
    }

    if (!('IntersectionObserver' in window) || !this.#targets.some(Boolean)) return;

    /** @type {Set<Element>} */
    const inView = new Set();

    // A section counts as current while it crosses the middle of the screen.
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) inView.add(entry.target);
          else inView.delete(entry.target);
        }

        const activeIndex = this.#targets.findIndex((target) => target && inView.has(target));
        links.forEach((link, index) => {
          if (index === activeIndex) link.setAttribute('aria-current', 'true');
          else link.removeAttribute('aria-current');
        });

        // Keep the current link in view in the scrolling row on phones, without moving the page.
        const active = links[activeIndex];
        const row = active?.closest('[data-bf26-jump-row]');
        if (active instanceof HTMLElement && row instanceof HTMLElement && row.scrollWidth > row.clientWidth) {
          const left = active.getBoundingClientRect().left - row.getBoundingClientRect().left + row.scrollLeft;
          row.scrollTo({ left: Math.max(0, left - 20), behavior: 'smooth' });
        }
      },
      { rootMargin: '-45% 0px -50% 0px' }
    );

    for (const target of this.#targets) {
      if (target) this.#observer.observe(target);
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#abortController?.abort();
    this.#abortController = null;
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  #queueStuck = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#updateStuck();
    });
  };

  #updateStuck() {
    const wrapper = this.parentElement;
    if (!wrapper) return;

    const height = this.offsetHeight;
    wrapper.style.setProperty('--bf26-jump-height', `${height}px`);

    const top = parseFloat(getComputedStyle(wrapper).top) || 0;
    const stuck = window.scrollY > 0 && wrapper.getBoundingClientRect().top <= top + 1;
    this.toggleAttribute('data-stuck', stuck);

    // Past the last linked section, a reveal bar steps aside again.
    const last = this.#targets.filter(Boolean).at(-1);
    this.toggleAttribute('data-past', Boolean(last && last.getBoundingClientRect().bottom < top + height));
  }
}

class Bf26Scroller extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  #frame = 0;

  connectedCallback() {
    const track = this.querySelector('[data-bf26-track]');
    const dots = Array.from(this.querySelectorAll('[data-bf26-dot]'));
    if (!(track instanceof HTMLElement) || dots.length < 2) return;

    this.#abortController = new AbortController();

    // Lights the dot of the card nearest the start of the row.
    const update = () => {
      this.#frame = 0;
      const cards = Array.from(track.querySelectorAll('bf26-card'));
      if (!cards.length) return;

      const start = track.getBoundingClientRect().left + (parseFloat(getComputedStyle(track).paddingLeft) || 0);
      let index = 0;
      let nearest = Infinity;
      cards.forEach((card, i) => {
        const distance = Math.abs(card.getBoundingClientRect().left - start);
        if (distance < nearest) {
          nearest = distance;
          index = i;
        }
      });
      if (track.scrollLeft > 0 && track.scrollLeft + track.clientWidth >= track.scrollWidth - 2) index = cards.length - 1;

      dots.forEach((dot, i) => dot.toggleAttribute('data-active', i === index));
    };

    track.addEventListener(
      'scroll',
      () => {
        if (!this.#frame) this.#frame = requestAnimationFrame(update);
      },
      { passive: true, signal: this.#abortController.signal }
    );
    update();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }
}

class Bf26Countdown extends HTMLElement {
  /** @type {ReturnType<typeof setInterval> | undefined} */
  #timer;

  connectedCallback() {
    const end = Date.parse(this.getAttribute('data-end') ?? '');
    if (Number.isNaN(end)) {
      this.hidden = true;
      return;
    }

    const output = this.querySelector('[data-bf26-countdown-value]');
    const days = this.getAttribute('data-days') ?? 'd';
    const hours = this.getAttribute('data-hours') ?? 'h';
    const minutes = this.getAttribute('data-minutes') ?? 'm';

    const tick = () => {
      const left = end - Date.now();
      if (left <= 0) {
        this.hidden = true;
        clearInterval(this.#timer);
        return;
      }

      const d = Math.floor(left / 86400000);
      const h = Math.floor(left / 3600000) % 24;
      const m = Math.floor(left / 60000) % 60;
      if (output) output.textContent = `${d}${days} ${String(h).padStart(2, '0')}${hours} ${String(m).padStart(2, '0')}${minutes}`;
      this.hidden = false;
    };

    tick();
    this.#timer = setInterval(tick, 30000);
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
  }
}

if (!customElements.get('bf26-card')) customElements.define('bf26-card', Bf26Card);
if (!customElements.get('bf26-jump-nav')) customElements.define('bf26-jump-nav', Bf26JumpNav);
if (!customElements.get('bf26-scroller')) customElements.define('bf26-scroller', Bf26Scroller);
if (!customElements.get('bf26-countdown')) customElements.define('bf26-countdown', Bf26Countdown);
